import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import type { PublicProgram, ProgramImage } from './api'
import { programApi } from './api'
import { getBrowserBallot } from '../audienceAward/programVoting'
import type { VoteStatus } from '../audienceAward/programVoting'
import './program.css'
export function ProgramView({
  program,
  preview = false,
}: {
  program: PublicProgram
  preview?: boolean
}) {
  const date = new Date(program.show.startsAt)
  const formatted = Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat('en-US', {
        dateStyle: 'full',
        timeStyle: 'short',
        timeZone: program.show.timeZone,
      }).format(date)
    : 'Date to be announced'
  return (
    <div
      className="digital-program"
      style={{ '--program-accent': program.branding.accent } as CSSProperties}
    >
      <a className="program-skip" href="#program-items">
        Skip to program
      </a>
      <header className="program-header">
        <a href="/" aria-label="One Day Dance home">
          <img
            src="/program/oddfest-2026/assets/one-day-dance.svg"
            alt="One Day Dance"
            width="300"
            height="54"
          />
        </a>
        <a href="#program-items">Explore the program ↓</a>
      </header>
      <main className="program-main">
        {preview && (
          <p className="program-preview" role="status">
            Draft preview, voting is disabled. Only approved fields appear.
          </p>
        )}
        <section className="program-hero" aria-labelledby="program-title">
          <div>
            <h1 id="program-title">{program.show.title}</h1>
            <h2>Digital program</h2>
            <p className="program-date">
              {formatted}
              <br />
              {program.show.venue}
            </p>
            {program.show.description && <p>{program.show.description}</p>}
            <a className="program-primary" href="#program-items">
              Explore the program ↓
            </a>
            {program.votingEnabled && (
              <a className="program-vote-link" href="#program-voting">
                Vote for the audience favorite ↓
              </a>
            )}
          </div>
          {(program.branding.hero || program.branding.logo) && (
            <div className="program-hero-art">
              {program.branding.hero && (
                <ProgramPicture
                  key={program.branding.hero.url}
                  image={program.branding.hero}
                  className="program-hero-image"
                  priority
                />
              )}
              {program.branding.logo && (
                <ProgramPicture
                  key={program.branding.logo.url}
                  image={program.branding.logo}
                  className="program-logo"
                />
              )}
            </div>
          )}
        </section>
        <section className="program-intro">
          <h2>The program</h2>
          <p>Follow the show order and open each piece’s credits to meet the artists behind it.</p>
          {program.items.length > 0 && (
            <details className="program-jump">
              <summary>Jump to a piece</summary>
              <ol>
                {program.items.map((item, index) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`}>
                      <span>{String(index + 1).padStart(2, '0')}</span>
                      {item.title}
                    </a>
                  </li>
                ))}
              </ol>
            </details>
          )}
        </section>
        <section id="program-items" aria-label="Program pieces">
          {program.items.length === 0 ? (
            <p className="program-empty">The lineup will be announced soon.</p>
          ) : (
            program.items.map((item, index) => (
              <article
                className={`program-piece ${item.image ? 'has-image' : ''}`}
                id={item.id}
                key={item.id}
              >
                {item.image && (
                  <ProgramPicture
                    key={item.image.url}
                    image={item.image}
                    className="program-still"
                  />
                )}
                <div className="program-piece-copy">
                  <div className="program-piece-heading">
                    <span className="program-number">{String(index + 1).padStart(2, '0')}</span>
                    <h3>{item.title}</h3>
                    {typeof item.durationSeconds === 'number' && (
                      <span className="program-runtime" aria-label="Duration">
                        {Math.floor(Math.round(item.durationSeconds) / 60)}:
                        {String(Math.round(item.durationSeconds) % 60).padStart(2, '0')}
                      </span>
                    )}
                  </div>
                  <p className="program-kind">
                    {item.pieceType === 'live' ? 'Live performance' : item.pieceType}
                  </p>
                  {item.creator && <p className="program-creator">{item.creator}</p>}
                  {item.description && <p className="program-description">{item.description}</p>}
                  {item.credits && (
                    <details>
                      <summary>Full credits</summary>
                      <div className="program-credits">{item.credits}</div>
                    </details>
                  )}
                  {item.biography && (
                    <details>
                      <summary>About the artist</summary>
                      <div className="program-credits">{item.biography}</div>
                    </details>
                  )}
                </div>
              </article>
            ))
          )}
        </section>
        {program.votingEnabled && (
          <AudienceBallot key={program.id} program={program} preview={preview} />
        )}
      </main>
      <footer className="program-footer">
        <p>
          {program.show.title} · {program.show.venue}
        </p>
        <a href="/">One Day Dance</a>
      </footer>
    </div>
  )
}
function AudienceBallot({ program, preview }: { program: PublicProgram; preview: boolean }) {
  const [token, setToken] = useState<string | null>(null)
  const [status, setStatus] = useState<VoteStatus>({ isOpen: false, submittedFilmId: null })
  const [message, setMessage] = useState(
    preview ? 'Preview only. No votes are collected.' : 'Checking voting availability…'
  )
  const [choice, setChoice] = useState('')
  const [reviewing, setReviewing] = useState(false)
  const [busy, setBusy] = useState(false)
  const eligible = program.items.filter((i) => i.awardEligible === true)
  const receiptTitle = program.items.find((i) => i.id === status.submittedFilmId)?.title
  useEffect(() => {
    if (preview) return
    let active = true
    try {
      const ballot = getBrowserBallot(localStorage, crypto, program.id)
      setToken(ballot)
      void programApi
        .status(program.id, ballot)
        .then((result) => {
          if (active) {
            setStatus(result)
            setMessage('')
          }
        })
        .catch(() => {
          if (active)
            setMessage('Voting could not be reached. Your program is still available. Try again.')
        })
    } catch {
      setMessage('Please enable browser storage to vote. Your program is still available.')
    }
    return () => {
      active = false
    }
  }, [program.id, preview])
  async function refresh() {
    if (!token || busy) return
    setBusy(true)
    try {
      setStatus(await programApi.status(program.id, token))
      setMessage('')
      setReviewing(false)
    } catch {
      setStatus((current) => ({ ...current, isOpen: false }))
      setMessage('Voting could not be reached. Try again.')
    } finally {
      setBusy(false)
    }
  }
  async function submit() {
    if (!token || !choice || busy || !status.isOpen || status.submittedFilmId) return
    setBusy(true)
    setMessage('Submitting your vote…')
    try {
      const receipt = await programApi.vote(program.id, token, choice)
      setStatus((current) => ({ ...current, submittedFilmId: receipt.filmId }))
      setMessage('')
      setReviewing(false)
    } catch {
      try {
        const result = await programApi.status(program.id, token)
        setStatus(result)
        setMessage(
          result.submittedFilmId ? '' : 'Your vote was not confirmed. Retry with this browser.'
        )
        if (result.submittedFilmId) setReviewing(false)
      } catch {
        setMessage('We could not confirm your vote. Keep this browser open and try again.')
      }
    } finally {
      setBusy(false)
    }
  }
  const locked = preview || busy || !status.isOpen || !!status.submittedFilmId
  return (
    <section className="program-ballot" id="program-voting" aria-labelledby="ballot-title">
      <h2 id="ballot-title">Audience favorite</h2>
      <p role="status" aria-live="polite">
        {message ||
          (status.submittedFilmId
            ? `Thank you. Your vote${receiptTitle ? ` for ${receiptTitle}` : ''} is recorded.`
            : status.isOpen
              ? 'Voting is open. Choose your favorite after the show.'
              : 'Voting is closed. Check back when voting opens.')}
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!locked && choice) setReviewing(true)
        }}
      >
        <fieldset disabled={locked}>
          <legend>Choose one piece</legend>
          {eligible.map((item) => (
            <label key={item.id} className="program-ballot-choice">
              <input
                type="radio"
                name="favorite"
                value={item.id}
                checked={(status.submittedFilmId ?? choice) === item.id}
                onChange={() => {
                  setChoice(item.id)
                  setReviewing(false)
                }}
              />
              <span>{item.title}</span>
            </label>
          ))}
        </fieldset>
        <button type="submit" disabled={locked || !choice}>
          Review my vote
        </button>
      </form>
      {reviewing && (
        <div className="program-confirm">
          <p>
            Submit your vote for <strong>{eligible.find((i) => i.id === choice)?.title}</strong>?
            Your choice cannot be changed after submission.
          </p>
          <button disabled={locked} onClick={() => void submit()}>
            Confirm vote
          </button>
          <button disabled={busy} onClick={() => setReviewing(false)}>
            Change choice
          </button>
        </div>
      )}
      <button disabled={preview || busy || !token} onClick={() => void refresh()}>
        Refresh voting
      </button>
    </section>
  )
}

function ProgramPicture({
  image,
  className,
  priority = false,
}: {
  image: ProgramImage
  className: string
  priority?: boolean
}) {
  const [missing, setMissing] = useState(false)
  const position = `${(image.focusX ?? 0.5) * 100}% ${(image.focusY ?? 0.5) * 100}%`
  if (missing)
    return (
      <div
        className={`${className} program-missing-image`}
        style={{ aspectRatio: `${image.width}/${image.height}` }}
        role="img"
        aria-label={`${image.alt}. Image unavailable.`}
      >
        Image unavailable
      </div>
    )
  return (
    <img
      className={className}
      src={image.url}
      alt={image.alt}
      width={image.width}
      height={image.height}
      style={{ objectPosition: position }}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      onError={() => setMissing(true)}
    />
  )
}
